import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { isAuthCallbackRoute } from '../lib/auth-callback-exchange';
import { AUTH_INTENDED_URL_KEY, formatAuthErrorMessage, getAuthCallbackUrl } from '../lib/auth-utils';
import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isConfigured: boolean;
  error: string | null;
  clearError: () => void;
  loginWithEmail: (credentials: { email: string; password: string }) => Promise<void>;
  registerWithEmail: (credentials: { email: string; password: string; name?: string }) => Promise<void>;
  loginWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  signOut: () => Promise<void>;
  openLogin: (context?: string) => void;
  closeLogin: () => void;
  loginOpen: boolean;
  loginContext: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginContext, setLoginContext] = useState<string | null>(null);

  const isConfigured = isSupabaseConfigured();

  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    let mounted = true;
    const onCallbackRoute = isAuthCallbackRoute();

    // On /auth/callback, skip getSession until AuthCallbackPage finishes PKCE exchange.
    // getSession() triggers _recoverAndRefresh() which can _removeSession() and wipe
    // the PKCE code_verifier before exchangeCodeForSession runs.
    if (!onCallbackRoute) {
      void supabase.auth.getSession().then(({ data, error: sessionError }) => {
        if (!mounted) return;
        if (sessionError) {
          setError(formatAuthErrorMessage(sessionError.message));
        }
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setIsLoading(false);
      });
    }

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setIsLoading(false);
    });

    if (onCallbackRoute) {
      setIsLoading(false);
    }

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const openLogin = useCallback((context?: string) => {
    setLoginContext(context ?? null);
    setLoginOpen(true);
    setError(null);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    setLoginContext(null);
  }, []);

  const loginWithEmail = useCallback(async (credentials: { email: string; password: string }) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setError('Supabase is not configured');
      throw new Error('Supabase is not configured');
    }
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: credentials.email.trim(),
      password: credentials.password,
    });
    if (signInError) {
      setError(formatAuthErrorMessage(signInError.message));
      throw signInError;
    }
    closeLogin();
  }, [closeLogin]);

  const registerWithEmail = useCallback(
    async (credentials: { email: string; password: string; name?: string }) => {
      const supabase = getSupabaseClient();
      if (!supabase) {
        setError('Supabase is not configured');
        throw new Error('Supabase is not configured');
      }
      setError(null);
      const { error: signUpError } = await supabase.auth.signUp({
        email: credentials.email.trim(),
        password: credentials.password,
        options: {
          data: credentials.name?.trim() ? { full_name: credentials.name.trim() } : undefined,
        },
      });
      if (signUpError) {
        setError(formatAuthErrorMessage(signUpError.message));
        throw signUpError;
      }
      closeLogin();
    },
    [closeLogin],
  );

  const loginWithOAuth = useCallback(async (provider: 'google' | 'github') => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setError('Supabase is not configured');
      return;
    }
    try {
      sessionStorage.setItem(AUTH_INTENDED_URL_KEY, window.location.pathname + window.location.search);
    } catch {
      /* ignore storage errors */
    }
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: getAuthCallbackUrl(),
        ...(provider === 'github' ? { scopes: 'read:user user:email' } : {}),
      },
    });
    if (oauthError) {
      setError(formatAuthErrorMessage(oauthError.message));
    }
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    await supabase.auth.signOut();
    closeLogin();
  }, [closeLogin]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      isLoading,
      isConfigured,
      error,
      clearError,
      loginWithEmail,
      registerWithEmail,
      loginWithOAuth,
      signOut,
      openLogin,
      closeLogin,
      loginOpen,
      loginContext,
    }),
    [
      user,
      session,
      isLoading,
      isConfigured,
      error,
      clearError,
      loginWithEmail,
      registerWithEmail,
      loginWithOAuth,
      signOut,
      openLogin,
      closeLogin,
      loginOpen,
      loginContext,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/** Returns false when auth is required but user is not signed in (opens login modal). */
export function useRequireAuth(actionContext: string): () => boolean {
  const { user, isLoading, isConfigured, openLogin } = useAuth();
  return useCallback(() => {
    if (!isConfigured) {
      return true;
    }
    if (isLoading) {
      return false;
    }
    if (!user) {
      openLogin(actionContext);
      return false;
    }
    return true;
  }, [user, isLoading, isConfigured, openLogin, actionContext]);
}
